import * as Y from 'yjs';
import { Observable } from 'lib0/observable';
import { assertCompatibleVaultStructure } from '../sync/vault-structure.js';

// Separate from older clients, which compact their caches without a guard.
export const userCacheName = userId => `orbital-user-v2-${userId}`;

function openDatabase(name, existingOnly = false) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(name);
    let absent = false, blocked = false;
    request.onupgradeneeded = () => {
      if (existingOnly) { absent = true; request.transaction.abort(); return; }
      request.result.createObjectStore('updates', { autoIncrement: true });
      request.result.createObjectStore('custom');
    };
    request.onerror = () => absent ? resolve(null) : reject(request.error);
    request.onblocked = () => {
      blocked = true;
      reject(new Error('Vault storage is busy in another tab'));
    };
    request.onsuccess = () => {
      const db = request.result;
      if (blocked) { db.close(); return; }
      db.onversionchange = () => db.close();
      resolve(db);
    };
  });
}

/** Read the original cache without writing, compacting, or deleting it. */
async function readLegacy(userId) {
  const db = await openDatabase(`orbital-user-${userId}`, true);
  if (!db) return null;
  try {
    if (!db.objectStoreNames.contains('updates')) throw new Error('Legacy vault updates store is missing');
    const records = await new Promise((resolve, reject) => {
      const tx = db.transaction('updates', 'readonly');
      const request = tx.objectStore('updates').getAll();
      tx.oncomplete = () => resolve(request.result);
      tx.onabort = () => reject(tx.error || new Error('Legacy vault read failed'));
      tx.onerror = () => reject(tx.error);
    });
    return records.length ? Y.mergeUpdates(records) : null;
  } finally { db.close(); }
}

/** A guarded read/merge/write transaction serializes vault saves across tabs.
 * A failed merge or transaction never replaces either persisted source.
 */
export class UserPersistence extends Observable {
  constructor(userId, doc) {
    super();
    this.userId = userId;
    this.name = userCacheName(userId);
    this.doc = doc;
    this.synced = false;
    this._destroyed = false;
    this._revision = 1;
    this._savedRevision = 0;
    this._chain = Promise.resolve();
    this._timer = null;
    this._update = (_bytes, origin) => {
      if (origin === this || this._destroyed) return;
      this._revision++;
      if (this.synced) this._schedule();
    };
    doc.on('update', this._update);
    this._db = null;
    this._retryingInit = null;
    this.whenSynced = this._init();
    this.whenSynced.catch(error => this.emit('error', [error]));
  }

  get hasPendingWrites() { return !this._destroyed && this._savedRevision < this._revision; }

  async _init() {
    this._db = openDatabase(this.name);
    try {
      // Observe both open/read failures immediately. The candidate snapshot is
      // committed only after both sources have been read and validated.
      const [, legacy] = await Promise.all([this._db, readLegacy(this.userId)]);
      await this._persist(legacy);
      this.synced = true;
      if (this.hasPendingWrites) this._schedule();
      this.emit('synced', [this]);
      return this;
    } catch (error) {
      // A retry needs a fresh handle (the initial open may itself have failed).
      await this._db.then(db => db.close(), () => {});
      throw error;
    }
  }

  _schedule() {
    clearTimeout(this._timer);
    this._timer = setTimeout(() => {
      this._timer = null;
      this._enqueue().catch(() => {});
    }, 100);
  }

  _enqueue() {
    const write = this._chain.then(() => this.hasPendingWrites && this._persist());
    this._chain = write.catch(error => this.emit('error', [error]));
    return write;
  }

  async _persist(legacy = null) {
    const db = await this._db;
    return new Promise((resolve, reject) => {
      const tx = db.transaction('updates', 'readwrite');
      const store = tx.objectStore('updates');
      const request = store.getAll();
      const candidate = new Y.Doc();
      let failure, snapshot, revision;
      request.onsuccess = () => {
        try {
          revision = this._revision;
          Y.applyUpdate(candidate, Y.encodeStateAsUpdate(this.doc));
          for (const update of [...request.result, ...(legacy ? [legacy] : [])]) {
            assertCompatibleVaultStructure(candidate, update);
            Y.applyUpdate(candidate, update);
          }
          snapshot = Y.encodeStateAsUpdate(candidate);
          // Both operations commit together, after validation, under IDB's
          // cross-tab transaction lock. An abort preserves the old records.
          store.clear();
          store.add(snapshot);
        } catch (error) { failure = error; tx.abort(); }
      };
      tx.onabort = () => { candidate.destroy(); reject(failure || tx.error || new Error('Vault save aborted')); };
      tx.onerror = () => { failure ||= tx.error; };
      tx.oncomplete = () => {
        candidate.destroy();
        // Edits made while the transaction ran are still in the live doc.
        // Yjs unions this snapshot; their newer revision triggers another save.
        try {
          assertCompatibleVaultStructure(this.doc, snapshot);
          Y.applyUpdate(this.doc, snapshot, this);
          this._savedRevision = revision;
          resolve();
        } catch (error) { reject(error); }
      };
    });
  }

  async flush() {
    clearTimeout(this._timer);
    this._timer = null;
    try {
      await this.whenSynced;
    } catch {
      // Disk space or permissions may have been restored. Repeat the complete
      // guarded read/merge/write; never skip an unread or conflicting source.
      if (!this._retryingInit) {
        this._retryingInit = this._init().finally(() => { this._retryingInit = null; });
        this.whenSynced = this._retryingInit;
        this.whenSynced.catch(error => this.emit('error', [error]));
      }
      await this.whenSynced;
    }
    await this._chain;
    while (this.hasPendingWrites) await this._enqueue();
  }

  async set(key, value) {
    await this.flush();
    const db = await this._db;
    return new Promise((resolve, reject) => {
      const tx = db.transaction('custom', 'readwrite');
      tx.objectStore('custom').put(value, key);
      tx.oncomplete = resolve;
      tx.onabort = () => reject(tx.error || new Error('Vault storage probe failed'));
      tx.onerror = () => reject(tx.error);
    });
  }

  async destroy() {
    if (this._destroyed) return;
    await this.flush();
    this._destroyed = true;
    this.doc.off('update', this._update);
    (await this._db).close();
    super.destroy();
  }
}
